import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Title from "../../components/Title";
import Loader from "../../components/Loader";
import useScrollToTop from "../../hooks/useScrollToTop";
import {
  getVoiceTalents,
  getVoiceTalentFilters,
} from "../../api/voiceTalents/getVoiceTalents";

// Renders a single (possibly multi-value / choice) field value for display.
const displayValue = (field, lng) => {
  const v = field.value;
  if (v === null || v === undefined) return "";
  if (Array.isArray(v))
    return v.map((x) => (x && x.label ? x.label[lng] : x)).join("، ");
  if (typeof v === "object" && v.label) return v.label[lng];
  return String(v);
};

const TalentCard = ({ talent, lng }) => (
  <div className="w-full sm:w-[334px] sh border-[2px] border-purpleLight rounded-xl p-[20px] flex flex-col gap-[12px] bg-white">
    {talent.public_code && (
      <span className="self-start text-sm bg-purpleLight text-white rounded-full px-[12px] py-[2px]">
        {talent.public_code}
      </span>
    )}

    {talent.fields.map((field) => (
      <div key={field.key} className="flex flex-col">
        <span className="text-gray text-sm">{field.label[lng]}</span>
        <span className="text-purple font-medium break-words">
          {displayValue(field, lng)}
        </span>
      </div>
    ))}

    {talent.samples && talent.samples.length > 0 && (
      <div className="flex flex-col gap-[8px] mt-[6px]">
        {talent.samples.map((sample) => (
          <div key={sample.id} className="flex flex-col gap-[4px]">
            {(sample.title || sample.performance_type) && (
              <span className="text-purple text-sm font-medium">
                {sample.title}
                {sample.performance_type
                  ? ` - ${sample.performance_type.label[lng]}`
                  : ""}
              </span>
            )}
            {sample.audio_url && (
              <audio controls preload="none" className="w-full">
                <source src={sample.audio_url} />
              </audio>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

const VoiceTalents = () => {
  const lng = useSelector((state) => state.lang.value);
  const { t } = useTranslation();
  useScrollToTop();

  const [loading, setLoading] = useState(true);
  const [filtersConfig, setFiltersConfig] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [search, setSearch] = useState("");
  const [talents, setTalents] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    getVoiceTalentFilters()
      .then(setFiltersConfig)
      .catch((error) => console.error("Error fetching filters", error));
  }, []);

  const fetchTalents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getVoiceTalents({
        filters: filterValues,
        q: search,
        page,
        perPage: 12,
      });
      setTalents(response.data);
      setMeta(response.meta);
    } catch (error) {
      console.error("Error fetching voice talents", error);
    } finally {
      setLoading(false);
    }
  }, [filterValues, search, page]);

  useEffect(() => {
    fetchTalents();
  }, [fetchTalents]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div dir={lng === "en" ? "ltr" : "rtl"} className="overflow-hidden">
      <div className="container mx-auto mt-[20px] mb-[140px] flex flex-col gap-[30px]">
        <div className="flex items-center justify-between flex-wrap gap-[20px]">
          <Title title={t("Voice Talents")} />
          <Link
            to="/voice-talents/register"
            className="bg-purple text-white rounded-full px-[24px] py-[10px] hover:bg-purpleLight transition-all"
          >
            {t("Join as a Voice Talent")}
          </Link>
        </div>

        <div className="flex flex-wrap items-end gap-[16px]">
          <div className="flex flex-col gap-[4px]">
            <label className="text-gray text-sm">{t("Search")}</label>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder={t("Search")}
              className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none"
            />
          </div>

          {filtersConfig.map((field) => (
            <div key={field.key} className="flex flex-col gap-[4px]">
              <label className="text-gray text-sm">{field.label[lng]}</label>
              <select
                value={filterValues[field.key] || ""}
                onChange={(e) => handleFilterChange(field.key, e.target.value)}
                className="border-[1px] border-purpleLight rounded-lg px-[12px] py-[8px] outline-none min-w-[160px]"
              >
                <option value="">{t("All")}</option>
                {field.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label[lng]}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>

        {loading ? (
          <Loader />
        ) : talents.length === 0 ? (
          <p className="text-center text-gray py-[60px]">
            {t("No voice talents found.")}
          </p>
        ) : (
          <div className="flex flex-wrap items-stretch justify-center sm:justify-start gap-[24px]">
            {talents.map((talent, index) => (
              <TalentCard
                key={talent.public_code || index}
                talent={talent}
                lng={lng}
              />
            ))}
          </div>
        )}

        {meta.last_page > 1 && (
          <div className="w-full flex items-center justify-center">
            <div className="text-purple w-[220px] font-medium flex items-center justify-between mt-[20px]">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={meta.current_page <= 1}
                className={`p-4 border-[1px] border-gray ${
                  meta.current_page <= 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {t("Prev")}
              </button>
              <span>
                {meta.current_page} / {meta.last_page}
              </span>
              <button
                onClick={() =>
                  setPage((p) => Math.min(meta.last_page, p + 1))
                }
                disabled={meta.current_page >= meta.last_page}
                className={`p-4 border-[1px] border-gray ${
                  meta.current_page >= meta.last_page
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                {t("Next")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceTalents;
